import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FaArrowLeft, FaBus, FaCheckCircle, FaChevronDown, FaChevronUp, FaDownload, FaMapMarkerAlt, FaPaperclip, FaPlane, FaTimes, FaTimesCircle, FaTrain } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { notifyError, notifySuccess, ToastContainer } from '../components/ErrorAlert';
import { NotifyMessages } from '../components/NotifyMessages';
import { UserContext } from '../UserContext';
import { decodeId } from '../utils/encrypt';
import { canEditBookingDetailsForAdmin, isAdminBookingLockedByManager, shouldShowAdminBookingAction } from '../utils/approvalAccess';
import { extractStoredFileName, openSecureFileUrl, resolveSecureFileUrl } from '../utils/fileAccess';
import './styles/InboxDetails.css';
import { INBOX_DETAILS_MESSAGES } from '../constants/inboxDetailsMessages';
import { IoChevronDown } from "react-icons/io5";

const formatDate = (value, withTime = false) => {
  if (!value) return 'NA';
  return new Date(value).toLocaleString(
    'en-IN',
    withTime
      ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short', year: 'numeric' }
  );
};

const sumExpense = (request) => {
  const ticketAmount = Number(request?.bookingDetails?.selfBookedTicketAmount || 0);
  const hotelAmount = Number(request?.bookingDetails?.selfBookedHotelAmount || 0);
  return ticketAmount + hotelAmount;
};

const workflowStatus = (request) => {
  if (!request) return 'Pending';
  const stage = request.currentStage;
  const reimbursementStatus = request.bookingDetails?.reimbursementStatus;
  const isExpenseRequest = Boolean(request.bookingDetails?.selfBooked);

  if (stage === 'PAYMENT') return 'Payment Pending';
  if (stage === 'PAYMENT_REJECTED') return 'Payment Rejected';
  if (isExpenseRequest && stage === 'COMPLETED' && reimbursementStatus === 'Approved') return 'Payment Approved';
  if (request.status === 'Declined' || request.status === 'Rejected') return 'Rejected';
  if (request.status === 'Booked') return 'Approved';
  if (stage === 'ADMIN') return 'Manager Approved';
  if (request.status === 'Approved') return isExpenseRequest ? 'Payment Approved' : 'Approved';
  return 'Pending';
};

const journeyStatusClass = (request) => {
  const status = workflowStatus(request);
  if (status === 'Approved') return 'approved';
  if (status === 'Rejected') return 'rejected';
  return 'pending';
};

const getCurrentStageLabel = (currentStage) => {
  if (currentStage === 'MANAGER') return 'Pending Manager Approval';
  if (currentStage === 'ADMIN') return 'Pending Admin Approval';
  if (currentStage === 'PAYMENT') return 'Payment Pending';
  if (currentStage === 'PAYMENT_REJECTED') return 'Payment Rejected';
  if (currentStage === 'MANAGER_REJECTED') return 'Rejected by Manager';
  if (currentStage === 'ADMIN_REJECTED') return 'Rejected by Admin';
  if (currentStage === 'COMPLETED') return 'Completed';

  return currentStage || 'NA';
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return 'NA';
  return `Rs. ${value}`;
};

const truncateText = (text, limit = 60) =>
  text.length > limit ? `${text.slice(0, limit).trim()}...` : text;

const getIndividualAgeLabel = (individual) => {
  if (individual?.age) return individual.age;
  if (!individual?.dob) return 'NA';

  const dob = new Date(individual.dob);
  if (Number.isNaN(dob.getTime())) return 'NA';

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? `${age} Years` : 'NA';
};

const getIndividualProofValue = (individual) =>
  individual?.idProof || individual?.existingIdProof || individual?.idProofUrl || '';

const capitalizeFirst = (text = '') =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const isImageFile = (fileName = '', url = '') =>
  /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(fileName || url);

const isPdfFile = (fileName = '', url = '') =>
  /\.pdf($|\?)/i.test(fileName || url);

const isCourierRequest = (request) =>
  Boolean(request?.supportNeeded && request.supportNeeded.includes('Courier'));

const isBookingDetailsComplete = (request) => {
  const details = request?.bookingDetails || {};

  if (isCourierRequest(request)) {
    return Boolean(
      details.bookingDate &&
      details.bookingBy &&
      details.amount &&
      details.podNumber &&
      details.courierCo &&
      details.fileUrl
    );
  }

  return Boolean(
    details.bookingDate &&
    details.bookingBy &&
    details.amount &&
    details.pnrNumber &&
    details.fileUrl
  );
};

const isAdministrationUser = (user) => {
  const department = String(user?.department || '').toLowerCase();
  const approvalRole = String(user?.approvalRole || '').toUpperCase();

  return department.includes('administration') || approvalRole === 'ADMIN_TEAM';
};

const InboxDetails = () => {
  const { token, user } = useContext(UserContext);
  const location = useLocation();
  const { requestId } = useParams();
  const realId = decodeId(requestId);
  const inboxSection = new URLSearchParams(location.search).get('section');
  const backToListTarget =
    ['expense', 'approval', 'tracking', 'notifications'].includes(inboxSection)
      ? `/inbox?section=${inboxSection}`
      : '/inbox';

  const [request, setRequest] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({
    general: false,
    requestDetails: false,
    passengers: false,
    booking: false,
    courierDetails: false,
    courierBookingInfo: false,
    status: false,
    attachments: false,
  });
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollAtBottom, setScrollAtBottom] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [approvalSupportingDocument, setApprovalSupportingDocument] = useState(null);
  const [loadError, setLoadError] = useState('');
  const detailsScrollRef = useRef(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [remarkModal, setRemarkModal] = useState(null);

  useEffect(() => {
    if (!realId) {
      setLoadError(INBOX_DETAILS_MESSAGES.INVALID_REQUEST_LINK);
      return;
    }

    if (!token) return;

    const fetchRequest = async () => {
      try {
        setLoadError('');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/${realId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(INBOX_DETAILS_MESSAGES.FETCH_REQUEST_DETAILS_FAILED);
        }

        const json = await response.json();
        setRequest(json);
      } catch (error) {
        console.error('Failed to load inbox request:', error);
        setLoadError(error.message || INBOX_DETAILS_MESSAGES.LOAD_REQUEST_DETAILS_FAILED);
      }
    };

    fetchRequest();
  }, [realId, token]);

  const isExpense = Boolean(request?.bookingDetails?.selfBooked);
  const managerApproved = Boolean(request?.managerApprovedAt);
  const managerRejected = request?.currentStage === 'MANAGER_REJECTED';
  const adminRejected = request?.currentStage === 'ADMIN_REJECTED';
  const bookingDetailsReady = useMemo(() => isBookingDetailsComplete(request), [request]);
  const adminBookingLocked = useMemo(() => isAdminBookingLockedByManager(request), [request]);
  const showBookingEditAction = useMemo(
    () => !isExpense && isAdministrationUser(user) && shouldShowAdminBookingAction(request),
    [isExpense, request, user]
  );
  const showEditBookingDetails = useMemo(
    () => !isExpense && isAdministrationUser(user) && canEditBookingDetailsForAdmin(request),
    [isExpense, request, user]
  );
  const canTakeAction = useMemo(() => {
    if (!request || !user?.email) return false;

    const viewerEmail = String(user.email).trim().toLowerCase();
    const requesterEmail = String(request.email || '').trim().toLowerCase();
    const currentStage = String(request.currentStage || '').trim().toUpperCase();

    // Requesters must never see approval actions on their own request.
    if (!viewerEmail || viewerEmail === requesterEmail) return false;

    // Manager actions are available only during the manager stage.
    // Admin actions are available only after the workflow reaches ADMIN.
    if (currentStage === 'MANAGER' && isAdministrationUser(user)) return false;
    if (currentStage === 'ADMIN' && !isAdministrationUser(user)) return false;
    if (!['MANAGER', 'ADMIN'].includes(currentStage)) return false;

    return request?.requestApprovers?.some(
      (approver) =>
        String(approver.approverEmail || '').trim().toLowerCase() === viewerEmail &&
        String(approver.decision || '').trim().toLowerCase() === 'pending' &&
        String(approver.stage || '').trim().toUpperCase() === currentStage
    );
  }, [request, user]);

  const canTakePaymentAction = useMemo(
    () => isExpense && request?.currentStage === 'PAYMENT_PENDING' && (
      request?.canTakePaymentAction === true || isAdministrationUser(user)
    ),
    [isExpense, request, user]
  );

  const attachments = useMemo(() => {
    if (!request?.bookingDetails) return [];

    const files = [
      { label: 'Ticket Bill', value: request.bookingDetails.selfBookedTicketBill },
      { label: 'Hotel Bill', value: request.bookingDetails.selfBookedHotelBill },
      { label: 'Ticket Details Document', value: request.bookingDetails.selfBookedTicketDetailsDoc },
      { label: 'Payment Proof', value: request.bookingDetails.paymentProof },
      ...(Array.isArray(request.bookingDetails?.bookingFiles)
      ? request.bookingDetails.bookingFiles.map((file, index) => ({
          label: request.supportNeeded?.includes('Courier')
            ? `Courier Attachment ${index + 1}`
            : `Booking Attachment ${index + 1}`,
          value: `${file.fileName}$${file.fileUrl}`,
        }))
      : []),
      { label: 'Cancellation Proof', value: request.bookingDetails.cancelProof },
      ...(request.individuals || []).map((individual, index) => ({
        label: `${individual.name || `Passenger ${index + 1}`} ID Proof`,
        value: getIndividualProofValue(individual),
      })),
    ].filter((item) => item.value);

    return files;
  }, [request]);

  const previewMode = useMemo(() => {
    if (!previewFile?.url) return '';
    if (isPdfFile(previewFile.name, previewFile.url)) return 'pdf';
    if (isImageFile(previewFile.name, previewFile.url)) return 'image';
    if (/\.docx$/i.test(previewFile.name)) {
      return "docx";
    }
    return 'document';
  }, [previewFile]);

  const handleDecision = async (decision) => {
    if (!request || !canTakeAction) return;
    setSaving(true);

    try {
      const endpoint = decision === 'approve' ? 'accept' : 'deny';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/requests/${request.id}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          decision === "approve"
            ? { remarks }
            : {
                remarks,
                declineReason,
              }
        ),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || INBOX_DETAILS_MESSAGES.UPDATE_APPROVAL_FAILED);
      }

      const updated = await response.json();
      setRequest(updated);
      setRemarks('');
      setDeclineReason('');
      setActionModal(null);
      notifySuccess(
        decision === 'approve'
          ? NotifyMessages.requestApprovedSuccessfully
          : NotifyMessages.requestRejectedSuccessfully,
        'bottom-center'
      );
    } catch (error) {
      console.error('Approval action failed:', error);
      notifyError(
        String(error.message || '').includes(INBOX_DETAILS_MESSAGES.UPDATE_BOOKING_DETAILS_FIRST)
          ? INBOX_DETAILS_MESSAGES.UPDATE_BOOKING_DETAILS_FIRST
          : String(error.message || INBOX_DETAILS_MESSAGES.UPDATE_APPROVAL_FAILED),
        'bottom-center'
      );
      if (String(error.message || '').includes(INBOX_DETAILS_MESSAGES.UPDATE_BOOKING_DETAILS_FIRST)) {
        setActionModal('booking');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleApproveClick = () => {
    if (!request) return;

    if (!isExpense && request.currentStage === 'ADMIN' && !bookingDetailsReady) {
      setActionModal('booking');
      return;
    }

    handleDecision('approve');
  };

  const handleRejectClick = () => {
    if (!remarks.trim() && !declineReason.trim()) {
      setActionModal('reject');
      return;
    }

    handleDecision('reject');
  };

  const handlePaymentDecision = async (decision) => {
    if (!request || !canTakePaymentAction) return;

    const isReject = decision === 'reject';
    if (isReject && !paymentRemarks.trim()) {
      return;
    }

    setSaving(true);
    try {
      const endpoint = decision === 'approve' ? 'approve' : 'reject';
      const requestOptions = {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (decision === 'approve') {
        const body = new FormData();
        body.append('remarks', paymentRemarks.trim());
        if (approvalSupportingDocument) {
          body.append('supportingDocument', approvalSupportingDocument);
        }
        requestOptions.body = body;
      } else {
        requestOptions.headers['Content-Type'] = 'application/json';
        requestOptions.body = JSON.stringify({ remarks: paymentRemarks.trim() });
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/${request.id}/payment/${endpoint}`,
        requestOptions
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || INBOX_DETAILS_MESSAGES.PAYMENT_APPROVAL_FAILED);
      }

      const updated = await response.json();
      setRequest(updated);
      setPaymentRemarks('');
      setApprovalSupportingDocument(null);
      setPaymentModal(null);
      notifySuccess(
        decision === 'approve'
          ? 'Payment approved successfully.'
          : 'Payment rejected successfully.',
        'bottom-center'
      );
    } catch (error) {
      console.error('Payment action failed:', error);
      notifyError(
        error.message ||
          (decision === 'approve'
            ? INBOX_DETAILS_MESSAGES.PAYMENT_APPROVAL_FAILED
            : INBOX_DETAILS_MESSAGES.PAYMENT_REJECTION_FAILED),
        'bottom-center'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleApprovePaymentClick = () => {
    if (!canTakePaymentAction) return;
    setPaymentModal('approve');
  };

  const handleRejectPaymentClick = () => {
    if (!canTakePaymentAction) return;
    setPaymentModal('reject');
  };

  const handleApprovalSupportingDocumentChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    const allowedExtensions = /\.(pdf|doc|docx|jpg|jpeg|png)$/i;
    const maxSize = 10 * 1024 * 1024;

    if (!allowedExtensions.test(file.name)) {
      notifyError('Supporting document must be PDF, DOC, DOCX, JPG, JPEG, or PNG.', 'bottom-center');
      return;
    }

    if (file.size > maxSize) {
      notifyError('Supporting document must be 10 MB or smaller.', 'bottom-center');
      return;
    }

    setApprovalSupportingDocument(file);
  };

  const handleApprovalSupportingDocumentDelete = () => {
    setApprovalSupportingDocument(null);
  };

  const handleDownload = async (fileValue) => {
    try {
      const secureUrl = await openSecureFileUrl(fileValue, token, false, true);

      const link = document.createElement("a");
      link.href = secureUrl;
      link.download = extractStoredFileName(fileValue);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download attachment:", error);
    }
  };

  const handlePreview = async (fileItem, index = 0) => {
    try {
      setPreviewLoading(true);
      setPreviewIndex(index);
      const secureUrl = await resolveSecureFileUrl(fileItem.value, token);
      setPreviewFile({
        name: extractStoredFileName(fileItem.value, fileItem.label),
        url: secureUrl,
      });
    } catch (error) {
      console.error("Failed to preview attachment:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const showNextAttachment = async () => {
    const nextIndex = (previewIndex + 1) % attachments.length;

    setPreviewLoading(true);

    const file = attachments[nextIndex];
    const secureUrl = await resolveSecureFileUrl(file.value, token);

    setPreviewIndex(nextIndex);

    setPreviewFile({
      name: extractStoredFileName(file.value, file.label),
      url: secureUrl,
    });

    setPreviewLoading(false);
  };

  const showPreviousAttachment = async () => {
    const prevIndex =
      (previewIndex - 1 + attachments.length) % attachments.length;

    setPreviewLoading(true);

    const file = attachments[prevIndex];
    const secureUrl = await resolveSecureFileUrl(file.value, token);

    setPreviewIndex(prevIndex);

    setPreviewFile({
      name: extractStoredFileName(file.value, file.label),
      url: secureUrl,
    });

    setPreviewLoading(false);
  };

  useEffect(() => {
    const container = detailsScrollRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const hasOverflow = container.scrollHeight > container.clientHeight + 8;
      const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 24;
      setShowScrollButton(hasOverflow);
      setScrollAtBottom(nearBottom);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [request, canTakeAction, canTakePaymentAction]);

  const handleScrollToggle = () => {
    const container = detailsScrollRef.current;
    if (!container) return;

    container.scrollTo({
      top: scrollAtBottom ? 0 : container.scrollHeight,
      behavior: 'smooth',
    });
  };

  if (!request) {
    return (
      <div className="InboxDetailsPage">
        <Header />
        <main className="inbox-details-main">
          <div className="inbox-details-card">
            {loadError || 'Loading request details...'}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="InboxDetailsPage">
      <ToastContainer />
      <Header />
      <main className="inbox-details-main">
        <div className="inbox-details-shell">
          <aside className="inbox-details-sidebar">
            <Link to={backToListTarget} className="back-link">
              <FaArrowLeft /> Back to List
            </Link>
            <div className="sidebar-summary">
              <div className="summary-id">REQUEST-{request.id}</div> 
                <div className="requester-row">
                  <div className="req-avatar">
                    {(request.user?.name || request.email || 'NA')
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase())
                      .join('')}
                  </div>
                  <div>
                    <div className="req-name">{request.user?.name || request.email || 'NA'}</div>
                    <div className="req-role">Requester</div>
                  </div>
                </div>
                <div className="stepper">
                <div className="stepper-label">Approval Progress</div>

                <div className="step done">
                  <div className="step-line"></div>
                  <div className="step-dot">✓</div>
                  <div className="step-text">
                    <div className="step-title">Request submitted</div>
                    <div className="step-sub">{formatDate(request.createdAt, true)}</div>
                  </div>
                </div>

                <div className={`step ${managerApproved || request.currentStage === 'ADMIN' || request.status === 'Approved' || request.status === 'Booked' ? 'done' : managerRejected ? 'rejected' : 'current'}`}>
                  <div className="step-line"></div>
                  <div className="step-dot">
                    {managerRejected
                      ? '✕'
                      : managerApproved ||
                        request.currentStage === 'ADMIN' ||
                        request.status === 'Approved' ||
                        request.status === 'Booked'
                        ? '✓'
                        : ''}
                  </div>
                  <div className="step-text">
                    <div className="step-title">{managerRejected ? 'Manager Rejected' : 'Manager approved'}</div>
                    {request.managerApprovedAt && <div className="step-sub">{formatDate(request.managerApprovedAt, true)}</div>}
                    {managerRejected && request.declinedAt && (
                      <div className="step-sub">
                        Rejected: {formatDate(request.declinedAt, true)}
                      </div>
                    )}

                    {managerRejected && request.declineReason && (
                      <div className="step-remark">
                        Reason: {request.declineReason}
                      </div>
                    )}
                    <div className="step-remark">
                      {request.managerRemarks?.length ? (
                        <>
                          Remark: {truncateText(request.managerRemarks.join(', '))}
                          {request.managerRemarks.join(', ').length > 60 && (
                            <button
                              type="button"
                              className="remark-show-more"
                              onClick={() => setRemarkModal({ title: 'Manager Remarks', text: request.managerRemarks.join(', ') })}
                            >
                              Show more
                            </button>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className={`step ${request.currentStage === 'PAYMENT_REJECTED' ? 'done' : request.adminApprovedAt || request.currentStage === 'PAYMENT' || request.currentStage === 'COMPLETED' ? 'done' : adminRejected ? 'rejected' : request.currentStage === 'ADMIN' ? 'current' : 'pending'}`}>
                  <div className="step-line"></div>
                  <div className="step-dot">
                    {adminRejected ? '✕' : request.adminApprovedAt || request.currentStage === 'PAYMENT' || request.currentStage === 'COMPLETED' ? '✓' : ''}
                  </div>
                  <div className="step-text">
                    <div className="step-title">{adminRejected ? 'Admin Rejected' : 'Admin approval'}</div>
                    {request.adminApprovedAt && <div className="step-sub">{formatDate(request.adminApprovedAt, true)}</div>}
                    {adminRejected && request.declinedAt && (
                      <div className="step-sub">Rejected: {formatDate(request.declinedAt, true)}</div>
                    )}
                    {adminRejected && request.declineReason && (
                      <div className="step-remark">Reason: {request.declineReason}</div>
                    )}
                    <div className="step-remark">
                      {request.adminRemarks?.length ? (
                        <>
                          Remark: {truncateText(request.adminRemarks.join(', '))}
                          {request.adminRemarks.join(', ').length > 60 && (
                            <button type="button" className="remark-show-more" onClick={() => setRemarkModal({ title: 'Admin Remarks', text: request.adminRemarks.join(', ') })}>
                              Show more
                            </button>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                {isExpense ? (
                  <>
                    <div className={`step ${request.currentStage === 'PAYMENT' ? 'current' : request.currentStage === 'PAYMENT_REJECTED' ? 'rejected' : request.currentStage === 'COMPLETED' && request.bookingDetails?.reimbursementStatus === 'Approved' ? 'done' : 'pending'}`}>
                      <div className="step-line"></div>
                      <div className="step-dot">
                        {request.currentStage === 'PAYMENT_REJECTED' ? '✕' : request.currentStage === 'COMPLETED' && request.bookingDetails?.reimbursementStatus === 'Approved' ? '✓' : ''}
                      </div>
                      <div className="step-text">
                        <div className="step-title">
                          {request.currentStage === 'PAYMENT_REJECTED' ? 'Payment rejected' : request.currentStage === 'PAYMENT' ? 'Payment pending' : 'Payment approved'}
                        </div>
                        {request.currentStage === 'PAYMENT' && <div className="step-remark">Waiting for payment approval</div>}
                        {request.bookingDetails?.reimbursementApprovedDate && request.currentStage === 'COMPLETED' && (
                          <div className="step-sub">{formatDate(request.bookingDetails.reimbursementApprovedDate, true)}</div>
                        )}
                        {request.bookingDetails?.reimbursementRejectedDate && request.currentStage === 'PAYMENT_REJECTED' && (
                          <div className="step-sub">{formatDate(request.bookingDetails.reimbursementRejectedDate, true)}</div>
                        )}
                        {request.bookingDetails?.reimbursementRejectedBy && request.currentStage === 'PAYMENT_REJECTED' && (
                          <div className="step-remark">By: {request.bookingDetails.reimbursementRejectedBy}</div>
                        )}
                        {request.currentStage === 'PAYMENT_REJECTED' && request.declineReason && (
                          <div className="step-remark">Reason: {request.declineReason}</div>
                        )}
                      </div>
                    </div>
                    <div className={`step ${request.currentStage === 'COMPLETED' && request.bookingDetails?.reimbursementStatus === 'Approved' ? 'done' : 'pending'}`}>
                      <div className="step-dot">{request.currentStage === 'COMPLETED' && request.bookingDetails?.reimbursementStatus === 'Approved' ? '✓' : ''}</div>
                      <div className="step-text">
                        <div className="step-title">Completed</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`step ${request.status === 'Booked' ? 'done' : 'pending'}`}>
                    <div className="step-dot">{request.status === 'Booked' ? '✓' : ''}</div>
                    <div className="step-text">
                      <div className="step-title">Booked</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>

          <section className="inbox-details-card">
            <div className="inbox-details-card-scroll" ref={detailsScrollRef}>
              <div className="details-toolbar">
                <div>
                  <p className="details-eyebrow">{isExpense ? 'Expense Request Details' : 'Approval Request Details'}</p>
                  <h1>{request.supportNeeded || 'Request Details'}</h1>
                </div>
                {attachments.length > 0 && (
                  <button
                    type="button"
                    className="download-summary-button"
                    onClick={() => handlePreview(attachments[0], 0)}
                  >
                    <FaPaperclip /> Preview attachment
                  </button>
                )}
              </div>

              <div className="detail-section">
                <button
                  type="button"
                  className="section-toggle"
                  onClick={() => toggleSection('general')}
                >
                  <div className="section-title-group">
                    <span className="section-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16v-4M12 8h.01M12 21a9 9 0 100-18 9 9 0 000 18z"/></svg>
                    </span>
                    <h3>General Information</h3>
                  </div>
                  <IoChevronDown
                    className={`accordion-arrow ${openSections.general ? "rotate" : ""}`}
                  />
                </button>
                  <div
                    className={`detail-grid accordion-content ${
                      openSections.general ? "open" : "closed"
                    }`}
                  >
                  <div><span>Employee Name</span><strong>{request.user?.name || request.email}</strong></div>
                  <div><span>Request ID</span><strong>{request.id}</strong></div>
                  <div><span>Batch ID</span><strong>{request.batchId || 'NA'}</strong></div>
                  <div><span>Requested On</span><strong>{formatDate(request.createdAt, true)}</strong></div>
                  <div><span>Status</span><strong>{workflowStatus(request)}</strong></div>
                  <div><span>Current Stage</span><strong>{getCurrentStageLabel(request.currentStage)}</strong></div>
                  <div><span>Support Needed</span><strong>{request.supportNeeded || 'NA'}</strong></div>
                  <div><span>Center</span><strong>{request.center || 'NA'}</strong></div>
                  <div><span>Client</span><strong>{request.client || 'NA'}</strong></div>
                  <div><span>Cost Center</span><strong>{request.costCenter || 'NA'}</strong></div>
                  <div><span>Purpose</span><strong>{request.purpose || 'NA'}</strong></div>
                  <div><span>Office Location</span><strong>{request.officeLocation || 'NA'}</strong></div>
                  <div><span>Requester Remarks</span><strong>{request.requesterRemarks || 'NA'}</strong></div>
                </div>
              </div>

              {isExpense ? (
                <>
                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('requestDetails')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 4h4v14H4V7h4l1-4z"/></svg></span>
                        <h3>Request Details</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.requestDetails ? "rotate" : ""}`} />
                    </button>
                    <div className={`detail-grid accordion-content ${openSections.requestDetails ? "open" : "closed"}`}>
                      <div><span>Source</span><strong>{request.source || 'NA'}</strong></div>
                      <div><span>Destination</span><strong>{request.destination || 'NA'}</strong></div>
                      <div><span>Travel Preference</span><strong>{request.travelPreference || 'NA'}</strong></div>
                      <div><span>Travel Class</span><strong>{request.travelClass || 'NA'}</strong></div>
                      <div><span>Start Date</span><strong>{formatDate(request.fromDate)}</strong></div>
                      <div><span>End Date</span><strong>{formatDate(request.toDate)}</strong></div>
                      <div><span>Train Number</span><strong>{request.trainNumber || 'NA'}</strong></div>
                      <div><span>Train Timing</span><strong>{request.trainTiming || 'NA'}</strong></div>
                      <div><span>Bus Name</span><strong>{request.busName || 'NA'}</strong></div>
                      <div><span>Bus Timing</span><strong>{request.busTiming || 'NA'}</strong></div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('passengers')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg></span>
                        <h3>Passengers Details {request.individuals?.length ? `(${request.individuals.length})` : ''}</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.passengers ? "rotate" : ""}`} />
                    </button>
                    <div className={`accordion-content ${openSections.passengers ? "open" : "closed"}`}>
                    {request.individuals?.length ? (
                      <div className="passenger-detailed-list">
                        <div className="passenger-stack">
                          {request.individuals.map((individual, index) => (
                            <div key={`${individual.id}-${individual.name || index}`} className="passenger-card">
                              <div className="passenger-card-top">
                                <div className="passenger-card-left">
                                  <div className="passenger-avatar">
                                    {(individual.name || `P${index + 1}`).charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="passenger-card-label">Passenger {index + 1}:</p>
                                    <p className="passenger-card-name">
                                      {capitalizeFirst(individual.name) || `Passenger ${index + 1}`}
                                      {individual.type && (
                                        <span className="passenger-type-badge"><FaCheckCircle /> {individual.type}</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="passenger-card-right-top">
                                  <div className="info-row"><span>ID Number:</span><strong>{individual.idNumber || 'NA'}</strong></div>
                                  <div className="info-row"><span>ID Type:</span><strong>{individual.idType || 'NA'}</strong></div>
                                </div>
                              </div>
                              <div className="passenger-card-divider" />
                              <div className="passenger-card-bottom">
                                <div className="passenger-card-bottom-left">
                                  <div className="info-row age-highlight"><span>Age:</span><strong>{getIndividualAgeLabel(individual)}</strong></div>
                                  <div className="info-row">
                                    <span>Contact Number:</span>
                                    <strong>{individual.contactNumber || 'NA'}</strong>
                                  </div>
                                  <div className="info-row">
                                    <span>ID Proof:</span>
                                    <strong>
                                      {getIndividualProofValue(individual) ? (
                                        <span className="id-proof-available"><FaCheckCircle /> Available</span>
                                      ) : 'NA'}
                                    </strong>
                                  </div>
                                </div>
                                <div className="passenger-card-bottom-right">
                                  <span className={`passenger-pill vertical ${(!individual.vertical || individual.vertical === 'NA') ? 'muted' : ''}`}>Vertical: {individual.vertical || 'NA'}</span>
                                  <span className={`passenger-pill project ${(!individual.project || individual.project === 'NA') ? 'muted' : ''}`}>Project: {individual.project || 'NA'}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <p>No passenger details available.</p>}
                    </div>
                  </div>

                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('booking')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg></span>
                        <h3>Booking Details</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.booking ? "rotate" : ""}`} />
                    </button>
                    <div className={`detail-grid accordion-content ${openSections.booking ? "open" : "closed"}`}>
                      <div><span>Booking Date</span><strong>{formatDate(request.bookingDetails?.bookingDate)}</strong></div>
                      <div><span>Booking By</span><strong>{request.bookingDetails?.bookingBy || 'NA'}</strong></div>
                      <div><span>Amount</span><strong>{formatAmount(request.bookingDetails?.amount)}</strong></div>
                      <div><span>PNR Number</span><strong>{request.bookingDetails?.pnrNumber || 'NA'}</strong></div>
                      <div><span>Reimbursement Status</span><strong>{request.bookingDetails?.reimbursementStatus || 'Pending'}</strong></div>
                      <div><span>Self Booked</span><strong>{request.bookingDetails?.selfBooked ? 'Yes' : 'No'}</strong></div>
                      <div><span>Ticket Amount</span><strong>{formatAmount(request.bookingDetails?.selfBookedTicketAmount)}</strong></div>
                      <div><span>Accommodation Amount</span><strong>{formatAmount(request.bookingDetails?.selfBookedHotelAmount)}</strong></div>
                      <div><span>Total Expenses</span><strong>{formatAmount(sumExpense(request) || request.bookingDetails?.amount || '')}</strong></div>
                      <div><span>Payment Mode</span><strong>{request.bookingDetails?.paymentMode || 'NA'}</strong></div>
                      <div><span>Payment Reference</span><strong>{request.bookingDetails?.paymentReference || 'NA'}</strong></div>
                      <div><span>Payment Date</span><strong>{formatDate(request.bookingDetails?.paymentDate)}</strong></div>
                      <div><span>Ticket Details</span><strong>{request.bookingDetails?.selfBookedTicketDetails || 'NA'}</strong></div>
                      <div><span>Accommodation Details</span><strong>{request.bookingDetails?.selfBookedHotelDetails || 'NA'}</strong></div>
                      <div><span>Reimbursement Approved By</span><strong>{request.bookingDetails?.reimbursementApprovedBy || 'NA'}</strong></div>
                      <div><span>Reimbursement Approved Date</span><strong>{formatDate(request.bookingDetails?.reimbursementApprovedDate)}</strong></div>
                      <div><span>Reimbursement Rejected By</span><strong>{request.bookingDetails?.reimbursementRejectedBy || 'NA'}</strong></div>
                      <div><span>Reimbursement Rejected Date</span><strong>{formatDate(request.bookingDetails?.reimbursementRejectedDate)}</strong></div>
                    </div>
                  </div>
                </>
              ) : request.supportNeeded?.includes('Courier') ? (
                <>
                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('booking')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 4h4v14H4V7h4l1-4z"/></svg></span>
                        <h3>Request Details</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.booking ? "rotate" : ""}`} />
                    </button>

                    <div className={`detail-grid accordion-content ${openSections.booking ? "open" : "closed"}`}>
                      <div><span>Courier Type</span><strong>{request.courierType || 'NA'}</strong></div>
                      <div><span>Office Location</span><strong>{request.officeLocation || 'NA'}</strong></div>
                      <div><span>Source Address</span><strong>{request.fromLocation || request.source || 'NA'}</strong></div>
                      <div><span>Destination Address</span><strong>{request.toLocation || request.destination || 'NA'}</strong></div>
                      <div><span>Sender Name</span><strong>{request.sentFrom || 'NA'}</strong></div>
                      <div><span>Receiver Name</span><strong>{request.receivedBy || 'NA'}</strong></div>
                      <div><span>Receiver Email</span><strong>{request.receiverEmail || 'NA'}</strong></div>
                      <div><span>Received Status</span><strong>{request.receivedStatus || 'NA'}</strong></div>
                      <div><span>Received On</span><strong>{formatDate(request.receivedOn)}</strong></div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="details-toolbar details-toolbar-no-margin toolbar-clickable" onClick={() => toggleSection('courierBookingInfo')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg></span>
                        <h3>Booking Details</h3>
                      </div>
                      <div className="toolbar-right-group">
                        <span className={`status-chip ${request.bookingDetails?.bookingStatus === 'Booked' ? 'approved' : 'pending'}`}>
                          {request.bookingDetails?.bookingStatus || 'Pending'}
                        </span>
                        {showBookingEditAction && (
                          showEditBookingDetails ? (
                            <Link
                              to={`/booking-details/${requestId}`}
                              state={{ returnTo: `/inbox/${requestId}` }}
                              className="download-summary-button"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Update/Edit Booking Details
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="download-summary-button"
                              disabled
                              title="Enabled after any assigned manager approves the request."
                              onClick={(e) => e.stopPropagation()}
                            >
                              Update/Edit Booking Details
                            </button>
                          )
                        )}
                        <IoChevronDown className={`accordion-arrow ${openSections.courierBookingInfo ? "rotate" : ""}`} />
                      </div>
                    </div>

                    {adminBookingLocked && (
                      <p className="detail-note">
                        Update Booking Details will be enabled after any assigned manager approves this request.
                      </p>
                    )}

                    <div className={`detail-grid accordion-content ${openSections.courierBookingInfo ? "open" : "closed"}`}>
                      <div><span>Booking Date</span><strong>{formatDate(request.bookingDetails?.bookingDate)}</strong></div>
                      <div><span>Booking By</span><strong>{request.bookingDetails?.bookingBy || 'NA'}</strong></div>
                      <div><span>Amount</span><strong>{formatAmount(request.bookingDetails?.amount)}</strong></div>
                      <div><span>POD Number</span><strong>{request.bookingDetails?.podNumber || 'NA'}</strong></div>
                      <div><span>Courier Company</span><strong>{request.bookingDetails?.courierCo || 'NA'}</strong></div>
                      <div><span>Booking Status</span><strong>{request.bookingDetails?.bookingStatus || 'Pending'}</strong></div>
                      <div><span>Cancellation Reason</span><strong>{request.bookingDetails?.cancelReason || 'NA'}</strong></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('requestDetails')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 4h4v14H4V7h4l1-4z"/></svg></span>
                        <h3>Request Details</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.requestDetails ? "rotate" : ""}`} />
                    </button>
                    <div className={`detail-grid accordion-content ${openSections.requestDetails ? "open" : "closed"}`}>
                      <div><span>Batch ID</span><strong>{request.batchId || 'NA'}</strong></div>
                      <div><span>Center</span><strong>{request.center || 'NA'}</strong></div>
                      <div><span>Client</span><strong>{request.client || 'NA'}</strong></div>
                      <div><span>Cost Center</span><strong>{request.costCenter || 'NA'}</strong></div>
                      <div><span>Purpose</span><strong>{request.purpose || 'NA'}</strong></div>
                      <div><span>Support Needed</span><strong>{request.supportNeeded || 'NA'}</strong></div>
                      <div><span>Requester Remarks</span><strong>{request.requesterRemarks || 'NA'}</strong></div>
                      {request.tripType && (
                    <div>
                      <span>Trip Type</span>
                      <strong>{request.tripType}</strong>
                    </div>
                  )}

{request.tripType === "Round Trip" ? (
  <div className="journey-columns">
    <div className="journey-card">
      <div className="ticket-header">
        <p className="ticket-title">Going Journey</p>
      </div>

      <div className="ticket-route">
        <div className="ticket-point-box">
          <p className="ticket-point-label"><FaMapMarkerAlt className="ticket-point-icon" /> Origin</p>
          <p className="ticket-place">{request.source || 'NA'}</p>
        </div>
        <div className="ticket-line">
          <span className="ticket-line-seg"></span>
          <span className="ticket-mode-icon">
            {request.travelPreference === "Train" ? <FaTrain /> : request.travelPreference === "Flight" ? <FaPlane /> : <FaBus />}
          </span>
          <span className="ticket-line-seg ticket-line-arrow"></span>
        </div>
        <div className="ticket-point-box">
          <p className="ticket-point-label"><FaMapMarkerAlt className="ticket-point-icon" /> Destination</p>
          <p className="ticket-place">{request.destination || 'NA'}</p>
        </div>
      </div>

      <div className="ticket-divider"></div>

      <div className="ticket-grid">
        <div>
          <p className="ticket-label">Date</p>
          <p className="ticket-value">{formatDate(request.fromDate)}</p>
        </div>
        <div>
          <p className="ticket-label">Time</p>
          <p className="ticket-value">
            {request.travelPreference === "Train" ? (request.trainTiming || 'NA') : request.travelPreference === "Bus" ? (request.busTiming || 'NA') : 'NA'}
          </p>
        </div>
        <div>
          <p className="ticket-label">{request.travelPreference === "Train" ? "Train No." : request.travelPreference === "Flight" ? "Flight No." : "Bus Name"}</p>
          <p className="ticket-value">
            {request.travelPreference === "Train" ? (request.trainNumber || 'NA') : request.travelPreference === "Bus" ? (request.busName || 'NA') : 'NA'}
          </p>
        </div>
        <div>
          <p className="ticket-label">Preference</p>
          <p className="ticket-value">{request.travelPreference || 'NA'}</p>
        </div>
        <div>
          <p className="ticket-label">Class</p>
          <p className="ticket-value">{request.travelClass || 'NA'}</p>
        </div>
      </div>
    </div>

    <div className="journey-card">
      <div className="ticket-header">
        <p className="ticket-title">Return Journey</p>
      </div>

      <div className="ticket-route">
        <div className="ticket-point-box">
          <p className="ticket-point-label">Origin</p>
          <p className="ticket-place">{request.returnSource || 'NA'}</p>
        </div>
        <div className="ticket-line">
          <span className="ticket-line-seg"></span>
          <span className="ticket-mode-icon">
            {request.returnTravelPreference === "Train" ? <FaTrain /> : request.returnTravelPreference === "Flight" ? <FaPlane /> : <FaBus />}
          </span>
          <span className="ticket-line-seg ticket-line-arrow"></span>
        </div>
        <div className="ticket-point-box">
          <p className="ticket-point-label">Destination</p>
          <p className="ticket-place">{request.returnDestination || 'NA'}</p>
        </div>
      </div>

      <div className="ticket-divider"></div>

      <div className="ticket-grid">
        <div>
          <p className="ticket-label">Date</p>
          <p className="ticket-value">{formatDate(request.returnDate)}</p>
        </div>
        <div>
          <p className="ticket-label">Time</p>
          <p className="ticket-value">
            {request.returnTravelPreference === "Train" ? (request.returnTrainTiming || 'NA') : request.returnTravelPreference === "Bus" ? (request.returnBusTiming || 'NA') : 'NA'}
          </p>
        </div>
        <div>
          <p className="ticket-label">{request.returnTravelPreference === "Train" ? "Train No." : request.returnTravelPreference === "Flight" ? "Flight No." : "Bus Name"}</p>
          <p className="ticket-value">
            {request.returnTravelPreference === "Train" ? (request.returnTrainNumber || 'NA') : request.returnTravelPreference === "Bus" ? (request.returnBusName || 'NA') : 'NA'}
          </p>
        </div>
        <div>
          <p className="ticket-label">Preference</p>
          <p className="ticket-value">{request.returnTravelPreference || 'NA'}</p>
        </div>
        <div>
          <p className="ticket-label">Class</p>
          <p className="ticket-value">{request.returnTravelClass || 'NA'}</p>
        </div>
      </div>
    </div>
  </div>
) : (
  <div className="journey-card">
    <div className="ticket-header">
      <p className="ticket-title">Journey Details</p>
    </div>

    <div className="ticket-route">
        <div className="ticket-point-box">
          <p className="ticket-point-label">Origin</p>
          <p className="ticket-place">{request.source || 'NA'}</p>
        </div>
        <div className="ticket-line">
          <span className="ticket-line-seg"></span>
          <span className="ticket-mode-icon">
            {request.travelPreference === "Train" ? <FaTrain /> : <FaBus />}
          </span>
          <span className="ticket-line-seg ticket-line-arrow"></span>
        </div>
        <div className="ticket-point-box">
          <p className="ticket-point-label">Destination</p>
          <p className="ticket-place">{request.destination || 'NA'}</p>
        </div>
      </div>

    <div className="ticket-divider"></div>

    <div className="ticket-grid">
      <div>
        <p className="ticket-label">Date</p>
        <p className="ticket-value">
          {request.supportNeeded === 'Travel' ? formatDate(request.fromDate) : `${formatDate(request.fromDate)} to ${formatDate(request.toDate)}`}
        </p>
      </div>
      <div>
        <p className="ticket-label">Time</p>
        <p className="ticket-value">
          {request.travelPreference === "Train" ? (request.trainTiming || 'NA') : request.travelPreference === "Bus" ? (request.busTiming || 'NA') : 'NA'}
        </p>
      </div>
      <div>
          <p className="ticket-label">{request.travelPreference === "Train" ? "Train No." : request.travelPreference === "Flight" ? "Flight No." : "Bus Name"}</p>
          <p className="ticket-value">
            {request.travelPreference === "Train" ? (request.trainNumber || 'NA') : request.travelPreference === "Bus" ? (request.busName || 'NA') : 'NA'}
          </p>
        </div>
      <div>
        <p className="ticket-label">Preference</p>
        <p className="ticket-value">{request.travelPreference || 'NA'}</p>
      </div>
      <div>
        <p className="ticket-label">Class</p>
        <p className="ticket-value">{request.travelClass || 'NA'}</p>
      </div>
    </div>
  </div>
)}
                    
                    </div>
                  </div>

                  <div className="detail-section">
                    <button type="button" className="section-toggle" onClick={() => toggleSection('passengers')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg></span>
                        <h3>Passengers Details {request.individuals?.length ? `(${request.individuals.length})` : ''}</h3>
                      </div>
                      <IoChevronDown className={`accordion-arrow ${openSections.passengers ? "rotate" : ""}`} />
                    </button>
                    <div className={`accordion-content ${openSections.passengers ? "open" : "closed"}`}>
                    {request.individuals?.length ? (
                      <div className="passenger-detailed-list">
                        <div className="passenger-stack">
                          {request.individuals.map((individual, index) => (
                            <div key={`${individual.id}-${individual.name || index}`} className="passenger-card">
                              <div className="passenger-card-top">
                                <div className="passenger-card-left">
                                  <div className="passenger-avatar">
                                    {(individual.name || `P${index + 1}`).charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="passenger-card-label">Passenger {index + 1}:</p>
                                    <p className="passenger-card-name">
                                      {capitalizeFirst(individual.name) || `Passenger ${index + 1}`}
                                      {individual.type && (
                                        <span className="passenger-type-badge"><FaCheckCircle /> {individual.type}</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="passenger-card-right-top">
                                  <div className="info-row"><span>ID Number:</span><strong>{individual.idNumber || 'NA'}</strong></div>
                                  <div className="info-row"><span>ID Type:</span><strong>{individual.idType || 'NA'}</strong></div>
                                </div>
                              </div>
                              <div className="passenger-card-divider" />
                              <div className="passenger-card-bottom">
                                <div className="passenger-card-bottom-left">
                                  <div className="info-row age-highlight"><span>Age:</span><strong>{getIndividualAgeLabel(individual)}</strong></div>
                                  <div className="info-row">
                                    <span>Contact Number:</span>
                                    <strong>{individual.contactNumber || 'NA'}</strong>
                                  </div>
                                  <div className="info-row">
                                    <span>ID Proof:</span>
                                    <strong>
                                      {getIndividualProofValue(individual) ? (
                                        <span className="id-proof-available"><FaCheckCircle /> Available</span>
                                      ) : 'NA'}
                                    </strong>
                                  </div>
                                </div>
                                <div className="passenger-card-bottom-right">
                                  <span className={`passenger-pill vertical ${!individual.vertical ? 'muted' : ''}`}>Vertical: {individual.vertical || 'NA'}</span>
                                  <span className={`passenger-pill project ${!individual.project ? 'muted' : ''}`}>Project: {individual.project || 'NA'}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <p>No passenger details available.</p>}
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="details-toolbar details-toolbar-no-margin toolbar-clickable" onClick={() => toggleSection('courierDetails')}>
                      <div className="section-title-group">
                        <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg></span>
                        <h3>Booking Details</h3>
                      </div>
                       <div className="toolbar-right-group">
                        <span className={`status-chip ${request.bookingDetails?.bookingStatus === 'Booked' ? 'approved' : 'pending'}`}>
                          {request.bookingDetails?.bookingStatus || 'Pending'}
                        </span>
                        {showBookingEditAction && (
                          showEditBookingDetails ? (
                            <Link
                              to={`/booking-details/${requestId}`}
                              state={{ returnTo: `/inbox/${requestId}` }}
                              className="download-summary-button"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Update/Edit Booking Details
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="download-summary-button"
                              disabled
                              title="Enabled after any assigned manager approves the request."
                              onClick={(e) => e.stopPropagation()}
                            >
                              Update/Edit Booking Details
                            </button>
                          )
                        )}
                        <IoChevronDown className={`accordion-arrow ${openSections.courierDetails ? "rotate" : ""}`} />
                      </div>
                    </div>
                    {adminBookingLocked && (
                      <p className="detail-note">Update Booking Details will be enabled after any assigned manager approves this request.</p>
                    )}
                    <div className={`detail-grid accordion-content ${openSections.courierDetails ? "open" : "closed"}`}>
                      <div><span>Status</span><strong>{request.status || 'Pending'}</strong></div>
                      <div><span>Booking Status</span><strong>{request.bookingDetails?.bookingStatus || 'Pending'}</strong></div>
                      <div><span>Self Booked</span><strong>{request.bookingDetails?.selfBooked ? 'Yes' : 'No'}</strong></div>
                      <div><span>Booking By</span><strong>{request.bookingDetails?.bookingBy || 'NA'}</strong></div>
                      <div><span>Booking Date</span><strong>{formatDate(request.bookingDetails?.bookingDate)}</strong></div>
                      <div><span>Amount</span><strong>{formatAmount(request.bookingDetails?.amount)}</strong></div>
                      <div><span>PNR Number</span><strong>{request.bookingDetails?.pnrNumber || 'NA'}</strong></div>
                      <div><span>Refund Amount</span><strong>{formatAmount(request.bookingDetails?.refundAmount)}</strong></div>
                      <div><span>Lost Amount</span><strong>{formatAmount(request.bookingDetails?.lostAmount)}</strong></div>
                      <div><span>Cancellation Reason</span><strong>{request.bookingDetails?.cancelReason || 'NA'}</strong></div>
                      <div><span>Admin Remarks</span><strong>{request.adminRemarks?.length ? request.adminRemarks.join(', ') : 'NA'}</strong></div>
                    </div>
                  </div>
                </>
              )}

              <div className="detail-section">
                <button type="button" className="section-toggle" onClick={() => toggleSection('status')}>
                  <div className="section-title-group">
                    <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 6v6l4 2"/></svg></span>
                    <h3>Status Details</h3>
                  </div>
                  <IoChevronDown className={`accordion-arrow ${openSections.status ? "rotate" : ""}`} />
                </button>
                <div className={`detail-grid accordion-content ${openSections.status ? "open" : "closed"}`}>
                  <div><span>Status</span><strong>{request.status || 'Pending'}</strong></div>
                  <div><span>Workflow Status</span><strong>{workflowStatus(request)}</strong></div>
                      <div><span>Current Stage</span><strong>{getCurrentStageLabel(request.currentStage)}</strong></div>
                  <div><span>Manager Approved By</span><strong>{request.managerApprovedBy || 'NA'}</strong></div>
                  <div><span>Manager Approved At</span><strong>{formatDate(request.managerApprovedAt, true)}</strong></div>
                  <div>
                    <span>Manager Remarks</span>
                    <strong>
                      {request.managerRemarks?.length
                        ? request.managerRemarks.join(', ')
                        : 'NA'}
                    </strong>
                  </div>
                  <div><span>Admin Approved By</span><strong>{request.adminApprovedBy || 'NA'}</strong></div>
                  <div><span>Admin Approved At</span><strong>{formatDate(request.adminApprovedAt, true)}</strong></div>
                  <div><span>Declined By</span><strong>{request.declinedBy || 'NA'}</strong></div>
                  <div><span>Declined At</span><strong>{formatDate(request.declinedAt, true)}</strong></div>
                  <div><span>Decline Reason</span><strong>{request.declineReason || 'NA'}</strong></div>
                  <div><span>Admin Remarks</span><strong>{request.adminRemarks?.length ? request.adminRemarks.join(', ') : 'NA'}</strong></div>
                </div>
              </div>

              {(!isCourierRequest(request) || isAdministrationUser(user)) && (
                <div className="detail-section">
                  <button type="button" className="section-toggle" onClick={() => toggleSection('attachments')}>
                    <div className="section-title-group">
                      <span className="section-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a5 5 0 01-7.07-7.07l9.19-9.19a3.5 3.5 0 014.95 4.95L10.13 17.1a2 2 0 01-2.83-2.83l8.49-8.48"/></svg></span>
                      <h3>Attachments {attachments.length ? `(${attachments.length})` : ''}</h3>
                    </div>
                    <IoChevronDown className={`accordion-arrow ${openSections.attachments ? "rotate" : ""}`} />
                  </button>
                  <div className={`accordion-content ${openSections.attachments ? "open" : "closed"}`}>
                  {attachments.length ? (
                    <div className="attachment-list">
                      {attachments.map((fileItem, index) => (
                        <div key={`${fileItem.label}-${fileItem.value}`} className="attachment-card">
                          <button
                            type="button"
                            className="attachment-item"
                            onClick={() => handlePreview(fileItem, index)}
                          >
                            <FaPaperclip />
                            <span>{fileItem.label}: {extractStoredFileName(fileItem.value, fileItem.label)}</span>
                          </button>
                          <button
                            type="button"
                            className="attachment-download"
                            onClick={() => handleDownload(fileItem.value)}
                          >
                            <FaDownload />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-attachments">No attachments uploaded.</p>
                  )}
                  </div>
                </div>
              )}

              </div>

            {canTakeAction && (
              <div className="approval-footer">
                <textarea
                  value={remarks}
                  onChange={(event) => setRemarks(event.target.value)}
                  placeholder="Write a remark to approve or reject this request..."
                  rows={2}
                  className="remarks-box remarks-box-inline"
                />
                <div className="approval-action-bar approval-action-bar-inline">
                  <button type="button" className="reject-button" disabled={saving} onClick={handleRejectClick}>
                    <FaTimesCircle /> Reject
                  </button>
                  <button type="button" className="approve-button" disabled={saving} onClick={handleApproveClick}>
                    <FaCheckCircle /> Approve
                  </button>
                </div>
              </div>
            )}

            {canTakePaymentAction && (
              <div className="approval-footer payment-approval-footer">
                <textarea
                  value={paymentRemarks}
                  onChange={(event) => setPaymentRemarks(event.target.value)}
                  placeholder="Write a remark to approve or reject payment..."
                  rows={2}
                  className="remarks-box remarks-box-inline"
                  disabled={saving}
                />
                <div className="approval-action-bar approval-action-bar-inline">
                  <button type="button" className="reject-button" disabled={saving} onClick={handleRejectPaymentClick}>
                    <FaTimesCircle /> Reject
                  </button>
                  <button type="button" className="approve-button" disabled={saving} onClick={handleApprovePaymentClick}>
                    <FaCheckCircle /> Approve
                  </button>
                </div>
              </div>
            )}

            {showScrollButton && (
              <button
                type="button"
                className="detail-scroll-button"
                onClick={handleScrollToggle}
                aria-label={scrollAtBottom ? 'Scroll to top' : 'Scroll to bottom'}
              >
                {scrollAtBottom ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            )}
          </section>
        </div>

        {(previewFile || previewLoading) && (
          <div className="attachment-preview-overlay" onClick={() => !previewLoading && setPreviewFile(null)}>
            <div className="attachment-preview-card" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="attachment-preview-close attachment-preview-close-floating" onClick={() => setPreviewFile(null)}>
                <FaTimes />
              </button>

              <button
                type="button"
                className="preview-arrow preview-left"
                onClick={showPreviousAttachment}
              >
                ❮
              </button>

              <button
                type="button"
                className="preview-arrow preview-right"
                onClick={showNextAttachment}
              >
                ❯
              </button>

              <div className="attachment-preview-body">
                {previewLoading ? (
                  <div className="attachment-preview-empty">Loading preview...</div>
                ) : previewMode === 'image' ? (
                  <div className="attachment-preview-scroll">
                    <img src={previewFile?.url} alt={previewFile?.name} className="attachment-preview-image" />
                  </div>
                ) : previewMode === "pdf" ? (
                  <div className="attachment-preview-scroll">
                    <iframe
                      title={previewFile?.name || "Attachment Preview"}
                      src={previewFile?.url}
                      className="attachment-preview-frame"
                    />
                  </div>
                ) : previewMode === "docx" ? (
                  <div className="attachment-preview-empty attachment-preview-docx-message">
                    <h3>Preview Not Available</h3>

                    <p>
                      DOCX files cannot be previewed in the browser at the moment.
                      Please download the document to view its contents.
                    </p>

                    <button
                      type="button"
                      className="attachment-download-button"
                      onClick={() => handleDownload(attachments[previewIndex].value)}
                    >
                      <FaDownload />
                      Download Document
                    </button>
                  </div>
                ) : previewMode === "document" ? (
                  <div className="attachment-preview-empty">
                    Preview is not available for this file type.
                  </div>
                ) : (
                  <div className="attachment-preview-empty">Preview is not available for this file.</div>
                )}
              </div>
              <div className="attachment-preview-counter">
                {previewIndex + 1} / {attachments.length}
              </div>
            </div>
          </div>
        )}

        {actionModal && (
          <div className="workflow-modal-overlay" onClick={() => { setActionModal(null) ; setDeclineReason('');}}>
            <div className="workflow-modal-card" onClick={(event) => event.stopPropagation()}>
              <h3>{actionModal === 'reject' ? 'Remarks Required' : 'Booking Details Required'}</h3>
              {actionModal === 'reject' ? (
                <>
                  <p>{INBOX_DETAILS_MESSAGES.REJECT_REMARKS_REQUIRED}</p>
                  <textarea
                    value={declineReason}
                    onChange={(event) => setDeclineReason(event.target.value)}
                    placeholder="Declined: reason"
                    rows={4}
                    className="remarks-box modal-remarks-box"
                  />
                  <div className="workflow-modal-actions">
                    <button type="button" className="reject-button" onClick={() =>{ setActionModal(null); setDeclineReason('');}}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="approve-button"
                      disabled={(!remarks.trim() && !declineReason.trim()) || saving}
                      onClick={() => handleDecision('reject')}
                    >
                      Confirm Reject
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>{INBOX_DETAILS_MESSAGES.UPDATE_BOOKING_DETAILS_FIRST}</p>
                  <div className="workflow-modal-actions">
                    <button type="button" className="reject-button" onClick={() => setActionModal(null)}>
                      Close
                    </button>
                    <Link
                      to={`/booking-details/${requestId}`}
                      state={{ returnTo: `/inbox/${requestId}` }}
                      className="approve-button workflow-modal-link"
                    >
                      Update Booking Details
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {paymentModal && (
          <div className="workflow-modal-overlay" onClick={() => !saving && setPaymentModal(null)}>
            <div className={`workflow-modal-card payment-modal-card ${paymentModal === 'reject' ? 'payment-reject-modal' : 'payment-approve-modal'}`} onClick={(event) => event.stopPropagation()}>
              <div className="payment-modal-header">
                <div>
                  <h3>{paymentModal === 'approve' ? 'Approve Payment' : 'Reject Payment'}</h3>
                  <p>{paymentModal === 'approve' ? 'Review the expense details before approving payment.' : 'Please provide a reason for rejecting this payment.'}</p>
                </div>
                <button type="button" className="payment-modal-close" onClick={() => setPaymentModal(null)} disabled={saving}>
                  <FaTimes />
                </button>
              </div>

              <div className="payment-summary-grid">
                <div><span>Employee Name</span><strong>{request.user?.name || request.email || 'NA'}</strong></div>
                <div><span>Request ID</span><strong>#{request.id}</strong></div>
                <div><span>Category</span><strong>{request.supportNeeded || 'Travel'}</strong></div>
              </div>

              {paymentModal === 'approve' ? (
                <>
                  {isExpense && (
                    <div className="payment-expense-review">
                      <div className="payment-expense-review-title">Self-Booking &amp; Reimbursement Details</div>
                      <div className="payment-expense-review-grid">
                        {(String(request.supportNeeded || '').includes('Travel') || request.bookingDetails?.selfBookedTicketBill || request.bookingDetails?.selfBookedTicketAmount) && (
                          <div className="payment-expense-review-card">
                            <div className="payment-expense-review-card-title">Ticket Details</div>
                            <div className="payment-expense-detail"><span>Ticket Amount</span><strong>{formatAmount(request.bookingDetails?.selfBookedTicketAmount)}</strong></div>
                          </div>
                        )}

                        {(String(request.supportNeeded || '').includes('Accommodation') || request.bookingDetails?.selfBookedHotelBill || request.bookingDetails?.selfBookedHotelAmount) && (
                          <div className="payment-expense-review-card">
                            <div className="payment-expense-review-card-title">Accommodation Details</div>
                            <div className="payment-expense-detail"><span>Accommodation Amount</span><strong>{formatAmount(request.bookingDetails?.selfBookedHotelAmount)}</strong></div>
                          </div>
                        )}

                        <div className="payment-expense-review-card">
                          <div className="payment-expense-review-card-title">Payment Details</div>
                          <div className="payment-expense-detail"><span>Payment Mode</span><strong>{request.bookingDetails?.paymentMode || 'NA'}</strong></div>
                          <div className="payment-expense-detail"><span>Payment Reference</span><strong>{request.bookingDetails?.paymentReference || 'NA'}</strong></div>
                          <div className="payment-expense-detail"><span>Paid Date</span><strong>{formatDate(request.bookingDetails?.paymentDate)}</strong></div>
                        </div>

                        <div className="payment-expense-review-card">
                          <div className="payment-expense-review-card-title">Reimbursement Payment Type</div>
                          <div className="payment-expense-detail"><span>Payment Type</span><strong>{request.bookingDetails?.reimbursementPaymentType || request.reimbursementPaymentType || 'NA'}</strong></div>
                          {(request.bookingDetails?.reimbursementPaymentType || request.reimbursementPaymentType || '').toUpperCase() === 'UPI' && <>
                            <div className="payment-expense-detail"><span>UPI ID</span><strong>{request.bookingDetails?.reimbursementUpiId || request.reimbursementUpiId || 'NA'}</strong></div>
                            <div className="payment-expense-detail"><span>UPI Number</span><strong>{request.bookingDetails?.reimbursementUpiNumber || request.reimbursementUpiNumber || request.bookingDetails?.paymentUpiNumber || 'NA'}</strong></div>
                          </>}
                          {(request.bookingDetails?.reimbursementPaymentType || request.reimbursementPaymentType || '').toUpperCase() === 'ACCOUNT DETAILS' && <>
                            <div className="payment-expense-detail"><span>Account Number</span><strong>{request.bookingDetails?.reimbursementAccountNumber || request.reimbursementAccountNumber || 'NA'}</strong></div>
                            <div className="payment-expense-detail"><span>Account Holder Name</span><strong>{request.bookingDetails?.reimbursementAccountHolderName || request.reimbursementAccountHolderName || 'NA'}</strong></div>
                          </>}
                        </div>
                      </div>

                      <div className="payment-supporting-document">
                        <div className="payment-expense-review-card-title">Supporting Document <span>(Optional)</span></div>
                        <label className="payment-upload-box">
                          <FaPaperclip />
                          <span>{approvalSupportingDocument ? approvalSupportingDocument.name : 'Upload supporting document'}</span>
                          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleApprovalSupportingDocumentChange} disabled={saving} />
                        </label>
                        <div className="payment-upload-help">PDF, DOC, DOCX, JPG, JPEG, PNG · Maximum 10 MB</div>
                        {approvalSupportingDocument && <button type="button" className="payment-remove-document" onClick={handleApprovalSupportingDocumentDelete} disabled={saving}><FaTimes /> Remove</button>}
                      </div>
                    </div>
                  )}

                  <div className="payment-review-note">The expense has already been approved by Manager and Administration. Approving here completes the payment workflow.</div>
                  <div className="workflow-modal-actions">
                    <button type="button" className="reject-button" onClick={() => setPaymentModal(null)} disabled={saving}>Cancel</button>
                    <button type="button" className="approve-button" onClick={() => handlePaymentDecision('approve')} disabled={saving}>
                      <FaCheckCircle /> Approve Payment
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className="payment-rejection-label">Rejection Reason <span>*</span></label>
                  <textarea
                    value={paymentRemarks}
                    onChange={(event) => setPaymentRemarks(event.target.value)}
                    placeholder="Please provide a reason for rejecting this payment..."
                    rows={4}
                    maxLength={500}
                    className="remarks-box modal-remarks-box"
                    disabled={saving}
                  />
                  <div className="payment-character-count">{paymentRemarks.length}/500</div>
                  <div className="payment-review-note payment-reject-note">This request will be returned to the employee with your remarks.</div>
                  <div className="workflow-modal-actions">
                    <button type="button" className="reject-button" onClick={() => setPaymentModal(null)} disabled={saving}>Cancel</button>
                    <button type="button" className="reject-button payment-confirm-reject" onClick={() => handlePaymentDecision('reject')} disabled={!paymentRemarks.trim() || saving}>
                      <FaTimesCircle /> Reject Payment
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {remarkModal && (
        <div className="workflow-modal-overlay" onClick={() => setRemarkModal(null)}>
          <div className="workflow-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{remarkModal.title}</h3>
            <p>{remarkModal.text}</p>
            <div className="workflow-modal-actions">
              <button type="button" className="reject-button" onClick={() => setRemarkModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
      <Footer />
    </div>
  );
};

export default InboxDetails;
